// starterPack.ts — the universal default measurement pack provisioned for a
// store at cold-start (Milestone D, hybrid timing: universal set early, the AI
// Assistant adds goal-tailored primitives at kickoff).
//
// These are deliberately UNIVERSAL: the cohorts use funnel steps + device (no
// store-specific selectors), and the one engagement event uses page scroll depth
// (no selector). Selector-based events (image views, etc.) are store-specific and
// are better added tailored at kickoff, so they're NOT in the universal pack.

import { cohortService } from '@/api/cohorts'
import { listCustomEvents, createCustomEvent } from '@/api/customEvents'

const TAG = 'starter-pack'

// Cohorts reference default-funnel step ids: '3' = Vista de Producto, '4' = Agregar
// al Carrito, 'cart_view' = Ver Carrito.
export const STARTER_COHORTS = [
  { name: 'Compradores mobile', description: 'Visitantes desde el celular', conditions: [{ type: 'device', value: 'mobile' }] },
  { name: 'Compradores desktop', description: 'Visitantes desde la computadora', conditions: [{ type: 'device', value: 'desktop' }] },
  { name: 'Vieron producto sin agregar al carrito', description: 'Llegaron a la PDP pero no agregaron al carrito', conditions: [{ type: 'funnel_step', step_id: '3' }, { type: 'not_funnel_step', step_id: '4' }] },
  { name: 'Abandonaron el carrito', description: 'Llegaron al carrito y no compraron', conditions: [{ type: 'funnel_step', step_id: 'cart_view' }, { type: 'not_converted' }] },
  { name: 'Convirtieron', description: 'Completaron una compra', conditions: [{ type: 'converted' }] },
]

export const STARTER_EVENTS = [
  {
    event_name: 'scroll_50_percent',
    display_name: 'Scroll 50% en la página',
    category: 'engagement' as const,
    tracking_config: { mode: 'auto' as const, auto_config: { interaction_type: 'scroll' as const, scroll_config: { depth_percentage: 50 } } },
  },
]

export interface StarterPackResult {
  cohortsCreated: number
  eventsCreated: number
  skipped: number
  failed: number
}

/** Idempotent: skips cohorts/events that already exist (by name). Safe to re-run. */
export async function provisionStarterPack(storeId: string): Promise<StarterPackResult> {
  const res: StarterPackResult = { cohortsCreated: 0, eventsCreated: 0, skipped: 0, failed: 0 }

  const [existingCohorts, existingEvents] = await Promise.all([
    cohortService.listCohorts('active').catch(() => []),
    listCustomEvents(storeId, 'active').then(r => r.custom_events).catch(() => []),
  ])
  const cohortNames = new Set(existingCohorts.map(c => c.name))
  const eventNames = new Set(existingEvents.map(e => e.event_name))

  for (const c of STARTER_COHORTS) {
    if (cohortNames.has(c.name)) { res.skipped++; continue }
    try { await cohortService.createCohort(c as any); res.cohortsCreated++ }
    catch { res.failed++ }
  }
  for (const e of STARTER_EVENTS) {
    if (eventNames.has(e.event_name)) { res.skipped++; continue }
    try { await createCustomEvent({ ...e, store_id: storeId, tags: [TAG] }); res.eventsCreated++ }
    catch { res.failed++ }
  }
  return res
}
